import { ActivityFilterKey, useWorkspaceAsset } from '@gripp/shared-logic';
import {
  ActivityFilterScreen,
  ActivityFilterScreenHandle,
  ActivityList,
  type ColumnData,
  SlideAction,
  SlideLayout,
  type SlideLayoutLocationState,
  ViewMode,
} from '@gripp/shared-ui';
import { useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ActivityFilters } from '../../activity/components/activityFilters';
import { Issue } from '../../issues/components/issue';
import { HeaderResetButton } from '../../shared/components/headerResetButton';
import { AssetRouteProvider } from '../assetRouteContext';
import {
  ALL_ACTIVITIES_QUERY_KEY,
  ALL_ACTIVITIES_QUERY_VALUE,
  RoutineSubmitSource,
} from '../assetUtils';
import { AssetCard } from './assetCard';
import { AssetFilters } from './assetFilters';

const ASSET_ACTIVITY_DETAIL_PATH_PATTERN =
  /^\/assets\/[^/]+\/activity\/[^/]+\/?$/;
const ACTIVITY_ASSET_ACTIVITY_DETAIL_PATH_PATTERN =
  /^\/activities\/[^/]+\/asset\/[^/]+\/activity\/[^/]+\/?$/;

const getAssetCardBackTarget = (pathname: string) => {
  const activitiesContextMatch = pathname.match(/^\/activities\/([^/]+)/);

  return activitiesContextMatch
    ? `/activities/${activitiesContextMatch[1]}`
    : '/assets';
};

const usesAssetCardLeftPanel = (
  pathname: string,
  search: string,
  from: RoutineSubmitSource | undefined
) => {
  if (from === RoutineSubmitSource.AssetCard) return true;

  const cameFromActivitiesAll =
    new URLSearchParams(search).get('from') === ALL_ACTIVITIES_QUERY_VALUE;

  return (
    !cameFromActivitiesAll &&
    (ASSET_ACTIVITY_DETAIL_PATH_PATTERN.test(pathname) ||
      ACTIVITY_ASSET_ACTIVITY_DETAIL_PATH_PATTERN.test(pathname))
  );
};

export const AssetActivity = () => {
  const { assetId, issueId: contextIssueId, issueId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { workspaceAsset } = useWorkspaceAsset(assetId ?? '', !assetId);
  const filterRef = useRef<ActivityFilterScreenHandle>(null);
  const [showFilters, setShowFilters] = useState(false);
  const locationState = location.state as SlideLayoutLocationState | null;
  const initialTransition =
    locationState?.slideLayoutInitialTransition === SlideAction.NEXT ||
    locationState?.slideLayoutInitialTransition === SlideAction.PREV
      ? locationState.slideLayoutInitialTransition
      : undefined;

  if (!assetId) {
    return null;
  }

  const from: RoutineSubmitSource | undefined = location.state?.from;
  const cameFromAsset = usesAssetCardLeftPanel(
    location.pathname,
    location.search,
    from
  );
  const handleLeftColumnBackPress = () => {
    if (cameFromAsset) {
      navigate(getAssetCardBackTarget(location.pathname), {
        state: { slideLayoutInitialTransition: SlideAction.PREV },
      });
      return;
    }

    navigate('../..', {
      relative: 'path',
      state: { slideLayoutInitialTransition: SlideAction.PREV },
    });
  };

  const leftColumn: ColumnData = {
    title: 'assets.menu.title',
    rawTitle: workspaceAsset?.asset?.name,
    mainComponent: cameFromAsset ? (
      <AssetCard assetId={assetId} forceShowing />
    ) : (
      <LeftColumn assetId={assetId} selectedIssue={issueId} />
    ),
    headerComponent: cameFromAsset ? (
      <AssetFilters assetId={assetId} />
    ) : (
      <ActivityFilters onFilterChange={() => setShowFilters(!showFilters)} />
    ),
    showBackButton: true,
    disableScroll: cameFromAsset ? false : true,
    // Return to the asset-context panel; the destination layout owns the
    // reverse handoff animation.
    onBackButtonPress: handleLeftColumnBackPress,
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent' as const,
    backButtonTransitionExitColumn: cameFromAsset ? 'both' : 'right',
  };

  const rightColumn = {
    title: 'assets.menu.title',
    showHeader: false,
    disableScroll: true,
    mainComponent: <Issue issueId={issueId} showAssetLink={false} />,
  };

  // Filter panel lives in the third column so the activity detail (rightColumn)
  // keeps its scroll position when the user opens and closes the filter panel.
  // Toggling showFilters to false from onBackButtonPress lets React re-derive
  // thirdColumn as undefined on the next render, which collapses the panel.
  const thirdColumn = showFilters
    ? {
        title: 'assets.menu.title',
        mainComponent: (
          <ActivityFilterScreen
            ref={filterRef}
            mode="page"
            isVisible={true}
            filterKey={ActivityFilterKey.AssetActivity}
            assetId={assetId}
            bottomBar={() => null}
          />
        ),
        headerComponent: (
          <HeaderResetButton onReset={() => filterRef.current?.onReset()} />
        ),
        showBackButton: true,
        onBackButtonPress: () => setShowFilters(false),
        disableScroll: true,
      }
    : undefined;

  return (
    <AssetRouteProvider
      value={{
        basePath: contextIssueId
          ? `/activities/${contextIssueId}/asset/${assetId}`
          : `/assets/${assetId}`,
        listPath: contextIssueId ? '/activities' : '/assets',
        isWorkspaceMode: false,
        issueId: undefined,
      }}
    >
      <SlideLayout
        leftColumn={leftColumn}
        rightColumn={rightColumn}
        thirdColumn={thirdColumn}
        viewMode={ViewMode.DUAL}
        initialTransition={initialTransition}
      />
    </AssetRouteProvider>
  );
};

const LeftColumn = ({
  assetId,
  selectedIssue,
}: {
  assetId: string;
  selectedIssue?: string;
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const onIssueSelected = (issueId: string) => {
    // When inside an activities route, preserve the full context prefix so the
    // selected activity stays within the activities chain. We read the outer
    // activity ID directly from the URL because useParams() can't reliably
    // surface it (the assetSubRoutes spread reuses :issueId for both the outer
    // and inner position, making the outer ID inaccessible via params).
    if (location.pathname.startsWith('/activities/')) {
      const assetSegment = `/asset/${assetId}`;
      const prefix = location.pathname.split(assetSegment)[0];
      navigate(
        `${prefix}${assetSegment}/activity/${issueId}?${ALL_ACTIVITIES_QUERY_KEY}`
      );
    } else {
      navigate(
        `/assets/${assetId}/activity/${issueId}?${ALL_ACTIVITIES_QUERY_KEY}`
      );
    }
  };

  return (
    <ActivityList
      filterKey={ActivityFilterKey.AssetActivity}
      onIssueSelected={onIssueSelected}
      assetId={assetId}
      showAssetName={false}
      showImage={false}
      pageSize={50}
      selectedIssue={selectedIssue}
      showTopBar={false}
    />
  );
};
